import { useState } from 'react'
import { Button } from "@/components/ui/button"
import type { PlayerPowerUp } from "@/types/games"
import { 
  Shield as ShieldIcon, 
  Timer as TimerIcon, 
  Zap as ZapIcon,
  SplitSquareHorizontal as SplitIcon,
  type LucideIcon
} from "lucide-react"

interface PowerUpBarProps {
  powerUps: PlayerPowerUp[]
  onUsePowerUp: (powerUpId: string) => Promise<boolean>
  disabled?: boolean
}

const POWER_UP_ICONS: Record<string, LucideIcon> = {
  'remove_answers': SplitIcon,
  'freeze_timer': TimerIcon,
  'multiply_score': ZapIcon,
  'extra_life': ShieldIcon
}

export function PowerUpBar({ 
  powerUps, 
  onUsePowerUp,
  disabled 
}: PowerUpBarProps) {
  const [usingPowerUp, setUsingPowerUp] = useState<string | null>(null)

  const handleUsePowerUp = async (powerUpId: string) => {
    if (usingPowerUp) return
    
    setUsingPowerUp(powerUpId)
    try {
      await onUsePowerUp(powerUpId)
    } finally {
      setUsingPowerUp(null)
    }
  }

  return (
    <div className="flex gap-2 mb-4">
      {powerUps.map((playerPowerUp) => {
        if (!playerPowerUp.powerup) return null
        
        const Icon = POWER_UP_ICONS[playerPowerUp.powerup.effect.type]
        const isUsing = usingPowerUp === playerPowerUp.powerup_id
        
        return (
          <Button
            key={playerPowerUp.powerup_id}
            variant="outline"
            size="icon"
            className="relative transition-all hover:scale-110 hover:shadow-lg hover:border-primary
                      bg-gradient-to-br from-background to-muted/50"
            disabled={disabled || isUsing || playerPowerUp.quantity === 0}
            onClick={() => handleUsePowerUp(playerPowerUp.powerup_id)}
            title={playerPowerUp.powerup.description}
          >
            {Icon && <Icon className={`h-4 w-4 ${isUsing ? 'animate-spin' : ''}`} />}
            <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs 
                           rounded-full w-5 h-5 flex items-center justify-center
                           shadow-lg animate-bounce">
              {playerPowerUp.quantity}
            </span>
          </Button>
        )
      })}
    </div>
  )
}